import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isBrowser } from "react-device-detect";
import { LoadingDots, ProjectCard } from "ui/components";
import { paths } from "ui/routes";
import { Project } from "core/models";
import { listProjects } from "core/services/site";
import { useSessionContext } from "ui/context/SessionContext";
import { cx } from "utils/join-classnames";
import styles from "./Projects.module.scss";
import pwa_styles from "./Projects_Pwa.module.scss";

const Projects: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const sessionContext = useSessionContext();

    const getProjects = async (): Promise<Project[]> => {
        if (!sessionContext.session?.token) {
            throw Error("Missing data for getProjects");
        }
        const res = await listProjects(sessionContext.session.token, 999);
        if (res.error || !res.value || res.value.length <= 0) {
            sessionContext.setError("Sorry, no projects found");
            navigate(paths.error);
            throw Error("Sorry, no projects found");
        }
        if (res.value.length === 1) {
            sessionContext.setSelectedProject(res.value[0]);
            sessionContext.setUserHasMultipleProjects(false);
            navigate(paths.latestFragments);
        } else {
            sessionContext.setUserHasMultipleProjects(true);
        }
        return res.value;
    };

    const { data: projects, status } = useQuery({
        queryKey: ["projects"],
        queryFn: getProjects,
    });

    const selectProject = async (project: Project) => {
        queryClient.removeQueries();
        sessionContext.setSelectedProject(project);
        navigate(paths.latestFragments);
    };

    return (
        <div className={styles.page}>
            {status === "pending" && (
                <div className={styles.title}>
                    Loading projects
                    <LoadingDots />
                </div>
            )}

            {status === "success" && (
                <>
                    {!sessionContext.selectedProject && (
                        <img
                            src="./static/img/logo-spoton.svg"
                            alt="SpotOn"
                            className={styles.logo}
                        />
                    )}
                    {!isBrowser && (
                        <div className={styles.title}>
                            {sessionContext.selectedProject
                                ? "Choose a project"
                                : `Choose a project to start`}
                        </div>
                    )}
                    {isBrowser && (
                        <div className={styles.title}>
                            {sessionContext.selectedProject
                                ? "Which project do you want to work on?"
                                : `Hi, choose a project to start`}
                        </div>
                    )}
                    {status === "success" && projects && isBrowser && (
                        <div
                            className={cx(
                                styles.projects__container,
                                projects.length > 15
                                    ? styles.projects__container__list
                                    : null,
                            )}
                        >
                            {projects.length <= 15 &&
                                projects.map((project) => (
                                    <ProjectCard
                                        project={project}
                                        active={
                                            project.id ===
                                            sessionContext.selectedProject?.id
                                        }
                                        onClick={selectProject}
                                    />
                                ))}
                            {projects.length > 15 &&
                                projects.map((project) => (
                                    <div
                                        className={cx(
                                            styles.listitem,
                                            project.id ===
                                                sessionContext.selectedProject
                                                    ?.id
                                                ? styles.listitemActive
                                                : null,
                                        )}
                                        role="presentation"
                                        onClick={() => selectProject(project)}
                                        key={project.id}
                                    >
                                        {project.name}
                                    </div>
                                ))}
                        </div>
                    )}
                    {status === "success" && projects && !isBrowser && (
                        <div
                            className={cx(
                                pwa_styles.projects__container,
                                projects.length > 15
                                    ? styles.projects__container__list
                                    : null,
                            )}
                        >
                            {projects.length <= 15 &&
                                projects.map((project) => (
                                    <ProjectCard
                                        project={project}
                                        active={
                                            project.id ===
                                            sessionContext.selectedProject?.id
                                        }
                                        onClick={selectProject}
                                        key={project.id}
                                    />
                                ))}
                            {projects.length > 15 &&
                                projects.map((project) => (
                                    <div
                                        className={cx(
                                            styles.listitem,
                                            project.id ===
                                                sessionContext.selectedProject
                                                    ?.id
                                                ? styles.listitemActive
                                                : null,
                                        )}
                                        role="presentation"
                                        onClick={() => selectProject(project)}
                                        key={project.id}
                                    >
                                        {project.name}
                                    </div>
                                ))}
                        </div>
                    )}
                </>
            )}
            {status === "error" && (
                <div className={cx(styles.title, styles.error)}>
                    Something went wrong while fetching your projects
                </div>
            )}
        </div>
    );
};

export default Projects;
